import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { motion } from "framer-motion";
import { 
  Search, Download, FileText, ChevronLeft, ChevronRight, 
  Eye, Pencil, Sparkles, Trash2, CheckSquare, Square, CheckCircle, Clock, Cloud,
  Landmark, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { Badge } from "../components/ui/badge";
import BankingStatusBadge from "../components/ui/banking-status-badge";
import PDFViewerModal from "../components/PDFViewerModal";
import DeleteInvoiceDialog from "../components/DeleteInvoiceDialog";
import SupplierOCRUploadModal from "../components/SupplierOCRUploadModal";
import OneDriveConnector from "../components/OneDriveConnector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    // Handle DD/MM/YYYY format
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        // Return as-is if already in DD/MM/YYYY
        return dateStr;
      }
    }
    // Handle YYYY-MM-DD format
    return new Date(dateStr).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
  } catch {
    return dateStr;
  }
};

// Years will be fetched dynamically from the data

// Months list
const months = [
  { value: "01", label: "Janvier" },
  { value: "02", label: "Février" },
  { value: "03", label: "Mars" },
  { value: "04", label: "Avril" },
  { value: "05", label: "Mai" },
  { value: "06", label: "Juin" },
  { value: "07", label: "Juillet" },
  { value: "08", label: "Août" },
  { value: "09", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

export default function FacturesFournisseurs() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [allFournisseurs, setAllFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filteredTotalTTC, setFilteredTotalTTC] = useState(0);
  
  // Batch selection
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || "");
  const [yearFilter, setYearFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [fournisseurFilter, setFournisseurFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // acquittee, non_acquittee, all
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Modals
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ocrUploadOpen, setOcrUploadOpen] = useState(false);
  const [showOneDrive, setShowOneDrive] = useState(false);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // State for available years (fetched from data)
  const [availableYears, setAvailableYears] = useState([]);
  
  // Fetch unique fournisseurs and years for filters
  const fetchFournisseurs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier-invoices?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const invoices = res.data.invoices || [];
      
      // Extract unique fournisseurs
      const uniqueFournisseurs = [...new Set(
        invoices
          .map(inv => inv.fournisseur)
          .filter(Boolean)
      )].sort();
      setAllFournisseurs(uniqueFournisseurs);
      
      // Extract unique years from invoice dates
      const uniqueYears = [...new Set(
        invoices
          .map(inv => inv.date?.substring(0, 4))
          .filter(Boolean)
      )].sort((a, b) => b - a); // Sort descending (recent first)
      setAvailableYears(uniqueYears);
    } catch (error) {
      console.error("Error fetching fournisseurs:", error);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: ((page - 1) * limit).toString(),
        limit: limit.toString(),
        search,
        year: yearFilter,
        month: monthFilter,
        fournisseur: fournisseurFilter,
        status: statusFilter
      });
      
      const res = await axios.get(`${API}/supplier-invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
      setFilteredTotalTTC(res.data.filtered_total_ttc || 0);
    } catch (error) {
      console.error("Error fetching supplier invoices:", error);
      toast.error("Erreur lors du chargement des factures");
    } finally {
      setLoading(false);
    }
  }, [token, page, search, yearFilter, monthFilter, fournisseurFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier-invoices/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    fetchFournisseurs();
  }, [fetchInvoices, fetchStats, fetchFournisseurs]);

  // Open invoice detail from URL parameter ?invoice=XXX
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId) {
      // Try to find in current list first
      const found = invoices.find(inv => inv.numero === invoiceId);
      if (found) {
        setSelectedInvoice(found);
        setSheetOpen(true);
      } else {
        // Fetch from API if not in current page
        const fetchInvoiceDetail = async () => {
          try {
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.get(`${API}/supplier-invoices/${encodeURIComponent(invoiceId)}`, { headers });
            if (res.data) {
              setSelectedInvoice(res.data);
              setSheetOpen(true);
            }
          } catch (err) {
            console.error("Failed to load invoice from URL:", err);
          }
        };
        fetchInvoiceDetail();
      }
    }
  }, [searchParams, token]);

  // Export Excel - respecte la sélection si présente
  const handleExport = () => {
    try {
      let exportData;
      
      // Si des factures sont sélectionnées, exporter uniquement celles-ci
      if (selectedInvoices.size > 0) {
        const selectedData = invoices.filter(inv => selectedInvoices.has(inv.numero));
        if (selectedData.length === 0) {
          toast.error("Aucune facture sélectionnée à exporter");
          return;
        }
        exportData = selectedData.map(inv => ({
          'Numéro': inv.numero,
          'Fournisseur': inv.fournisseur,
          'Date': inv.date,
          'Échéance': inv.echeance || '',
          'Catégorie': inv.categorie || '',
          'Total HT': inv.total_ht,
          'TVA': inv.total_tva,
          'Total TTC': inv.total_ttc,
          'Statut': inv.statut_paiement,
          'Mode paiement': inv.mode_paiement || '',
          'PDF': inv.has_pdf ? 'Oui' : 'Non'
        }));
        toast.success(`${exportData.length} factures sélectionnées exportées`);
      } else {
        if (invoices.length === 0) {
          toast.error("Aucune facture à exporter");
          return;
        }
        exportData = invoices.map(inv => ({
          'Numéro': inv.numero,
          'Fournisseur': inv.fournisseur,
          'Date': inv.date,
          'Échéance': inv.echeance || '',
          'Catégorie': inv.categorie || '',
          'Total HT': inv.total_ht,
          'TVA': inv.total_tva,
          'Total TTC': inv.total_ttc,
          'Statut': inv.statut_paiement,
          'Mode paiement': inv.mode_paiement || '',
          'PDF': inv.has_pdf ? 'Oui' : 'Non'
        }));
        toast.success(`${exportData.length} factures exportées`);
      }
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Factures Fournisseurs");
      
      let filename = 'factures_fournisseurs';
      if (selectedInvoices.size > 0) filename += '_selection';
      filename += `_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export Excel");
    }
  };

  // Export PDF - télécharge les PDFs des factures sélectionnées ou toutes
  const handleExportPDF = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Déterminer les factures à exporter
      let invoicesToExport;
      if (selectedInvoices.size > 0) {
        invoicesToExport = invoices.filter(inv => selectedInvoices.has(inv.numero) && inv.has_pdf);
      } else {
        invoicesToExport = invoices.filter(inv => inv.has_pdf);
      }
      
      if (invoicesToExport.length === 0) {
        toast.error("Aucune facture avec PDF à exporter");
        return;
      }
      
      toast.info(`Export de ${invoicesToExport.length} PDF en cours...`);
      
      // Si une seule facture, télécharger directement
      if (invoicesToExport.length === 1) {
        const inv = invoicesToExport[0];
        const response = await axios.get(`${API}/supplier-invoices/${inv.numero}/pdf`, {
          headers,
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `facture_fournisseur_${inv.numero}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('PDF téléchargé');
        return;
      }
      
      // Si plusieurs factures, créer un ZIP
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let successCount = 0;
      for (const inv of invoicesToExport) {
        try {
          const response = await axios.get(`${API}/supplier-invoices/${inv.numero}/pdf`, {
            headers,
            responseType: 'blob'
          });
          zip.file(`facture_${inv.fournisseur}_${inv.numero}.pdf`, response.data);
          successCount++;
        } catch (e) {
          console.error(`Erreur téléchargement PDF ${inv.numero}:`, e);
        }
      }
      
      if (successCount === 0) {
        toast.error("Aucun PDF n'a pu être récupéré");
        return;
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factures_fournisseurs_${selectedInvoices.size > 0 ? 'selection' : 'export'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${successCount} PDFs exportés dans le ZIP`);
    } catch (error) {
      console.error("Export PDF error:", error);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  // Edit mode functions
  const startEditing = () => {
    setEditData({
      numero: selectedInvoice.numero || '',
      fournisseur: selectedInvoice.fournisseur || '',
      date: selectedInvoice.date || '',
      echeance: selectedInvoice.echeance || '',
      total_ht: selectedInvoice.total_ht || 0,
      total_tva: selectedInvoice.total_tva || 0,
      total_ttc: selectedInvoice.total_ttc || 0,
      categorie: selectedInvoice.categorie || '',
      mode_paiement: selectedInvoice.mode_paiement || '',
      notes: selectedInvoice.notes || ''
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-calculate TTC when HT or TVA changes
      if (field === 'total_ht' || field === 'total_tva') {
        const ht = field === 'total_ht' ? parseFloat(value) || 0 : parseFloat(prev.total_ht) || 0;
        const tva = field === 'total_tva' ? parseFloat(value) || 0 : parseFloat(prev.total_tva) || 0;
        updated.total_ttc = ht + tva;
      }
      return updated;
    });
  };

  const saveChanges = async () => {
    try {
      setIsSaving(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Prepare update data - only send changed fields
      const updateData = {};
      Object.keys(editData).forEach(key => {
        if (editData[key] !== selectedInvoice[key]) {
          updateData[key] = editData[key];
        }
      });
      
      if (Object.keys(updateData).length === 0) {
        toast.info("Aucune modification à enregistrer");
        setIsEditing(false);
        return;
      }
      
      const response = await axios.put(
        `${API}/supplier-invoices/${selectedInvoice.numero}`,
        updateData,
        { headers }
      );
      
      if (response.data.success) {
        toast.success("Facture mise à jour avec succès");
        
        // Update local state
        const updatedInvoice = response.data.invoice;
        setSelectedInvoice(updatedInvoice);
        setInvoices(prev => prev.map(inv => 
          inv.numero === selectedInvoice.numero ? updatedInvoice : inv
        ));
        
        setIsEditing(false);
        
        // Refresh the list if numero changed
        if (updateData.numero) {
          fetchInvoices();
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setIsSaving(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  // Batch selection handlers
  const toggleSelectInvoice = (numero, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(numero)) {
      newSelected.delete(numero);
    } else {
      newSelected.add(numero);
    }
    setSelectedInvoices(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.size === invoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(invoices.map(inv => inv.numero)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedInvoices.size} facture(s) fournisseur ?`)) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API}/supplier-invoices/batch-delete`,
        { numeros: Array.from(selectedInvoices) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.deleted_count} facture(s) supprimée(s)`);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} facture(s) n'ont pas pu être supprimées`);
      }
      
      setSelectedInvoices(new Set());
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error("Batch delete error:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="supplier-invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-8 h-8 text-primary" />
            Factures Fournisseurs
          </h1>
          <p className="text-muted-foreground mt-1">
            {total} facture{total !== 1 ? 's' : ''} fournisseur{total !== 1 ? 's' : ''}
            {selectedInvoices.size > 0 && (
              <span className="ml-2 text-primary font-medium">
                • {selectedInvoices.size} sélectionnée(s)
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {selectedInvoices.size > 0 && (
            <Button 
              variant="destructive"
              onClick={handleBatchDelete}
              disabled={isDeleting}
              data-testid="batch-delete-btn"
              size="sm"
              className="flex-1 sm:flex-none"
            >
              <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
              {isDeleting ? 'Suppression...' : `Supprimer (${selectedInvoices.size})`}
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={() => setShowOneDrive(true)}
            data-testid="onedrive-btn"
            size="sm"
            className="flex-1 sm:flex-none border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <Cloud className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">OneDrive</span>
            <span className="sm:hidden">☁️</span>
          </Button>
          <Button 
            variant="default"
            onClick={() => setOcrUploadOpen(true)}
            data-testid="ocr-upload-btn"
            size="sm"
            className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white"
          >
            <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Import OCR</span>
            <span className="sm:hidden">OCR</span>
          </Button>
          <Button 
            onClick={handleExport}
            variant="outline"
            data-testid="export-btn"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            <Download className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exporter Excel {selectedInvoices.size > 0 ? `(${selectedInvoices.size})` : ''}</span>
            <span className="sm:hidden">Export</span>
          </Button>
          <Button 
            onClick={handleExportPDF}
            className="btn-primary bg-primary text-primary-foreground flex-1 sm:flex-none"
            data-testid="export-pdf-btn"
            size="sm"
          >
            <FileText className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Exporter PDF {selectedInvoices.size > 0 ? `(${selectedInvoices.size})` : ''}</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards - Only shown when year or month filter is active */}
      {(yearFilter !== "all" || monthFilter !== "all") && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Total TTC <span className="text-primary">(période filtrée)</span>
              </p>
              <p className="text-xl md:text-2xl font-bold text-primary tabular-nums mt-1">
                {formatCurrency(filteredTotalTTC)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4 md:p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Nombre de factures <span className="text-primary">(période filtrée)</span>
              </p>
              <p className="text-xl md:text-2xl font-bold tabular-nums mt-1">{total}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par numéro ou fournisseur..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            
            <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-32" data-testid="year-filter">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={monthFilter} onValueChange={(v) => { setMonthFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-36" data-testid="month-filter">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={fournisseurFilter} onValueChange={(v) => { setFournisseurFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-48" data-testid="fournisseur-filter">
                <SelectValue placeholder="Fournisseur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les fournisseurs</SelectItem>
                {allFournisseurs.map(fournisseur => (
                  <SelectItem key={fournisseur} value={fournisseur}>{fournisseur}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-44" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="acquittee">Acquittée</SelectItem>
                <SelectItem value="non_acquittee">Non acquittée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 border-b border-slate-200">
                  <TableHead className="w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid="select-all-checkbox"
                    >
                      {selectedInvoices.size === invoices.length && invoices.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-primary" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Numéro</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fournisseur</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">TTC</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Statut</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Statut Bancaire</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Aucune facture fournisseur trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice, idx) => (
                    <TableRow 
                      key={invoice.numero}
                      className={`cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 ${selectedInvoices.has(invoice.numero) ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setSheetOpen(true);
                      }}
                      data-testid={`invoice-row-${idx}`}
                    >
                      <TableCell className="w-10">
                        <button
                          onClick={(e) => toggleSelectInvoice(invoice.numero, e)}
                          className="p-1 hover:bg-slate-200 rounded"
                          data-testid={`select-invoice-${idx}`}
                        >
                          {selectedInvoices.has(invoice.numero) ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium text-primary">
                        {invoice.numero}
                      </TableCell>
                      <TableCell className="font-medium">{invoice.fournisseur}</TableCell>
                      <TableCell className="tabular-nums">{formatDate(invoice.date)}</TableCell>
                      <TableCell className="text-right font-mono font-medium tabular-nums">
                        {formatCurrency(invoice.total_ttc)}
                      </TableCell>
                      <TableCell className="text-center">
                        {invoice.paiement ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            <CheckCircle className="w-3 h-3 mr-1" />Acquittée
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            <Clock className="w-3 h-3 mr-1" />Non acquittée
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <BankingStatusBadge
                          statut_paiement={invoice.statut_paiement}
                          rapprochement_status={invoice.rapprochement_status}
                          echeance={invoice.echeance}
                          compact={true}
                        />
                      </TableCell>
                      <TableCell>
                        {invoice.has_pdf && <FileText className="w-4 h-4 text-muted-foreground" />}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Détail Facture Fournisseur
            </SheetTitle>
          </SheetHeader>
          
          {selectedInvoice && (
            <div className="mt-6 space-y-6">
              {/* Actions */}
              <div className="flex gap-2">
                {selectedInvoice.has_pdf && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setPdfViewerOpen(true)}
                    data-testid="view-pdf-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Voir PDF
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => {
                    setSheetOpen(false);
                    setDeleteDialogOpen(true);
                  }}
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>

              {/* Edit/View Toggle Buttons */}
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={startEditing}
                    data-testid="edit-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={cancelEditing}
                      disabled={isSaving}
                    >
                      Annuler
                    </Button>
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={saveChanges}
                      disabled={isSaving}
                      data-testid="save-btn"
                    >
                      {isSaving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </>
                )}
              </div>

              {/* Invoice Info - Editable or Read-only */}
              <div className="space-y-4">
                {/* Numéro */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <p className="text-xs uppercase text-primary/70 mb-1">Numéro</p>
                  {isEditing ? (
                    <Input
                      value={editData.numero}
                      onChange={(e) => handleEditChange('numero', e.target.value)}
                      className="font-mono font-bold"
                      data-testid="edit-numero"
                    />
                  ) : (
                    <p className="text-lg font-mono font-bold text-primary">{selectedInvoice.numero}</p>
                  )}
                </div>

                {/* Fournisseur & Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Fournisseur</p>
                    {isEditing ? (
                      <Input
                        value={editData.fournisseur}
                        onChange={(e) => handleEditChange('fournisseur', e.target.value)}
                        data-testid="edit-fournisseur"
                      />
                    ) : (
                      <p className="font-medium">{selectedInvoice.fournisseur}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Catégorie</p>
                    {isEditing ? (
                      <Input
                        value={editData.categorie}
                        onChange={(e) => handleEditChange('categorie', e.target.value)}
                        placeholder="Ex: Téléphonie, Fournitures..."
                        data-testid="edit-categorie"
                      />
                    ) : (
                      <p className="font-medium">{selectedInvoice.categorie || selectedInvoice.type || 'Facture'}</p>
                    )}
                  </div>
                </div>

                {/* Date & Échéance */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Date</p>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editData.date}
                        onChange={(e) => handleEditChange('date', e.target.value)}
                        data-testid="edit-date"
                      />
                    ) : (
                      <p className="font-medium">{formatDate(selectedInvoice.date)}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Échéance</p>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editData.echeance}
                        onChange={(e) => handleEditChange('echeance', e.target.value)}
                        data-testid="edit-echeance"
                      />
                    ) : (
                      <p className="font-medium">{formatDate(selectedInvoice.echeance) || '-'}</p>
                    )}
                  </div>
                </div>

                {/* Mode paiement */}
                <div>
                  <p className="text-xs uppercase text-muted-foreground mb-1">Mode paiement</p>
                  {isEditing ? (
                    <Select value={editData.mode_paiement} onValueChange={(v) => handleEditChange('mode_paiement', v)}>
                      <SelectTrigger data-testid="edit-mode-paiement">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="virement">Virement</SelectItem>
                        <SelectItem value="prelevement">Prélèvement</SelectItem>
                        <SelectItem value="cheque">Chèque</SelectItem>
                        <SelectItem value="cb">Carte bancaire</SelectItem>
                        <SelectItem value="especes">Espèces</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">{selectedInvoice.mode_paiement || '-'}</p>
                  )}
                </div>

                {/* Montants */}
                {isEditing ? (
                  <div className="p-4 rounded-lg border bg-slate-50 space-y-3">
                    <p className="text-xs uppercase text-muted-foreground font-semibold">Montants</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">HT</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.total_ht}
                          onChange={(e) => handleEditChange('total_ht', e.target.value)}
                          className="font-mono"
                          data-testid="edit-total-ht"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">TVA</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.total_tva}
                          onChange={(e) => handleEditChange('total_tva', e.target.value)}
                          className="font-mono"
                          data-testid="edit-total-tva"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">TTC</p>
                        <Input
                          type="number"
                          step="0.01"
                          value={editData.total_ttc}
                          onChange={(e) => handleEditChange('total_ttc', e.target.value)}
                          className="font-mono bg-slate-100"
                          data-testid="edit-total-ttc"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Notes */}
                {isEditing && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Notes</p>
                    <textarea
                      value={editData.notes}
                      onChange={(e) => handleEditChange('notes', e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                      rows={2}
                      placeholder="Notes ou commentaires..."
                      data-testid="edit-notes"
                    />
                  </div>
                )}

                {/* Statut de rapprochement bancaire */}
                <div className={`p-4 rounded-lg border space-y-3 ${
                  selectedInvoice.paiement 
                    ? 'bg-green-50 border-green-200' 
                    : selectedInvoice.echeance && new Date(selectedInvoice.echeance) < new Date()
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase text-muted-foreground font-semibold flex items-center gap-2">
                      <Landmark className="w-4 h-4" />
                      Statut Bancaire
                    </p>
                    <BankingStatusBadge
                      statut_paiement={selectedInvoice.statut_paiement}
                      rapprochement_status={selectedInvoice.rapprochement_status}
                      echeance={selectedInvoice.echeance}
                    />
                  </div>
                  
                  {selectedInvoice.bridge_transaction_id ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transaction liée</span>
                        <span className="font-mono text-xs">{selectedInvoice.bridge_transaction_id}</span>
                      </div>
                      {selectedInvoice.date_paiement && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date décaissement</span>
                          <span className="text-green-600 font-medium">
                            {typeof selectedInvoice.date_paiement === 'string' && selectedInvoice.date_paiement.includes('/') 
                              ? selectedInvoice.date_paiement 
                              : new Date(selectedInvoice.date_paiement).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                          </span>
                        </div>
                      )}
                      {selectedInvoice.libelle_releve && (
                        <div className="pt-2 border-t border-green-200">
                          <p className="text-xs text-muted-foreground mb-1">Libellé bancaire</p>
                          <p className="font-medium text-sm">{selectedInvoice.libelle_releve}</p>
                        </div>
                      )}
                      <a 
                        href={`/rapprochement-v2/transactions?id=${selectedInvoice.bridge_transaction_id}`}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Voir la transaction
                      </a>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm text-muted-foreground mb-2">Aucune transaction liée</p>
                      <a 
                        href={`/rapprochement-v2/pending`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        <Search className="w-3 h-3" />
                        Chercher une transaction
                      </a>
                    </div>
                  )}
                </div>

                {/* Amounts */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total HT</span>
                    <span className="font-mono">{formatCurrency(selectedInvoice.total_ht)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">TVA</span>
                    <span className="font-mono">{formatCurrency(selectedInvoice.total_tva)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Total TTC</span>
                    <span className="font-mono font-bold text-lg">{formatCurrency(selectedInvoice.total_ttc)}</span>
                  </div>
                </div>

                {/* Lines */}
                {selectedInvoice.lines?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-2">Lignes de facturation</p>
                    <div className="space-y-2">
                      {selectedInvoice.lines.map((line, idx) => (
                        <div key={line.description + idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                          <p className="font-medium">{line.description}</p>
                          <div className="flex justify-between text-muted-foreground mt-1">
                            <span>{line.quantity} x {formatCurrency(line.unit_price)}</span>
                            <span className="font-mono">{formatCurrency(line.amount_ht || line.total)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PDF Button */}
                {selectedInvoice.has_pdf && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSheetOpen(false);
                      setPdfViewerOpen(true);
                    }}
                    data-testid="view-pdf-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Voir le PDF
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        open={pdfViewerOpen}
        onOpenChange={setPdfViewerOpen}
        invoiceNumero={selectedInvoice?.numero}
        invoiceId={selectedInvoice?.id}
        title={`Facture ${selectedInvoice?.numero}`}
        pdfEndpoint="supplier-invoices"
      />

      {/* Delete Dialog */}
      <DeleteInvoiceDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        invoice={selectedInvoice}
        onSuccess={() => {
          fetchInvoices();
          fetchStats();
          setSelectedInvoice(null);
        }}
        apiEndpoint="supplier-invoices"
      />

      {/* OCR Upload Modal */}
      <SupplierOCRUploadModal
        open={ocrUploadOpen}
        onOpenChange={setOcrUploadOpen}
        onSuccess={() => {
          fetchInvoices();
          fetchStats();
          fetchFournisseurs();
        }}
      />

      {/* OneDrive Sheet */}
      <Sheet open={showOneDrive} onOpenChange={setShowOneDrive}>
        <SheetContent className="w-[450px] sm:w-[500px]">
          <SheetHeader>
            <SheetTitle>Connecteur OneDrive</SheetTitle>
            <SheetDescription>
              Importez automatiquement vos factures depuis OneDrive
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <OneDriveConnector 
              onImportSuccess={() => {
                fetchInvoices();
                fetchStats();
                fetchFournisseurs();
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
